import { FormGroup } from '@angular/forms';
import type { FormField } from '../../interfaces/form.interface';
import { ReactiveChangeOriginPolicy, type ReactiveChangeOrigin } from '../change-origin/reactive-change-origin';
import type { ReactiveDependencyGraph } from '../dependencies/reactive-dependency-graph';
import { DropdownData, FilteredValue, InputType } from '../../interfaces/input.interface';
import { ReactiveFormValueUtils, type ReactiveDropdownOption } from '../../model/reactive-form-value.utils';
import { resolveReactiveDropdownDependencyBehaviour } from '../dropdown/reactive-dropdown-dependency-behaviour';
import { DropdownSelectedValueService } from './dropdown-selected-value.service';
import { FormOperationState } from './form-operation-state';

/** Applies dropdown filters and keeps dependent selections coherent. */
export class DropdownFilterExecutor<T> {
    constructor(
        private readonly model: FormGroup,
        private readonly state: FormOperationState,
        private readonly selectedValueService: DropdownSelectedValueService<T>,
        private readonly dependencyGraph: ReactiveDependencyGraph<T>,
    ) {}

    public applyFilter(field: FormField<T>, changedField: string | null, origin: ReactiveChangeOrigin): boolean {
        const dropdownData = field.input.data as DropdownData<T>;

        if (typeof field.input?.dropdownConfiguration?.filter === 'function') {
            const parentValue = changedField ? this.model.get(changedField)?.value : undefined;
            const currentValue = this.model.get(field.name ?? '')?.value;
            const filteredOptions = ((dropdownData.values || []) as FilteredValue<T>[]).filter((option) => {
                const context = {
                    option,
                    field,
                    model: this.model,
                    changedField,
                    parentValue,
                    currentValue,
                };

                return field.input.dropdownConfiguration?.filter?.(option, context) !== false;
            });

            dropdownData.filtered = this.mergeSelectedOptionsWhenNeeded(field, dropdownData, filteredOptions, currentValue);
        }

        return this.applyDependentSelectionState(field, dropdownData, changedField, origin);
    }

    /**
     * Filters dropdown values by the id of another dropdown.
     */
    public getFilteredValues(previousValue: string, field: FormField<T>): (T | null)[] | NonNullable<T> | string {
        const ids = new Set(previousValue.split(';').map((value) => value.trim()));
        const valuePrimaryKey = ReactiveFormValueUtils.getFieldControlValueKey(field);
        const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);
        const filteredValues = (field.input.data as DropdownData<T>).filtered
            .filter((value: FilteredValue<T>) => {
                const comparableId = ReactiveFormValueUtils.extractComparableId(value.value, valuePrimaryKey, identityKeys);

                return comparableId !== null && ids.has(String(comparableId));
            })
            .map((value: FilteredValue<T>) => value.value);

        return this.isMultiSelect(field) ? filteredValues : filteredValues[0] ?? '';
    }

    private applyDependentSelectionState(
        field: FormField<T>,
        dropdownData: DropdownData<T>,
        changedField: string | null,
        origin: ReactiveChangeOrigin,
    ): boolean {
        if (!this.isFilterDependencyChange(field, changedField)) {
            return false;
        }

        const controlName = field.name ?? '';
        const control = this.model.get(controlName);

        if (!control) {
            return false;
        }

        const filteredOptions = this.getSelectableFilteredOptions(dropdownData);
        const keepValueUntilOptionsLoaded = this.shouldKeepValueUntilFilteredOptionsAreReady(field, filteredOptions, changedField);
        const mustClearBecauseDependencyChanged = !keepValueUntilOptionsLoaded
            && this.mustClearBecauseDependencyChanged(field, changedField, origin);
        const nextValue = mustClearBecauseDependencyChanged
            ? this.getClearedControlValue(field)
            : this.getRetainedControlValue(field, control.value, filteredOptions);
        const shouldPatchControl = this.shouldPatchControlValue(
            field,
            control.value,
            nextValue,
            mustClearBecauseDependencyChanged,
        );

        this.selectedValueService.applySelected(field, nextValue);

        if (!shouldPatchControl || this.state.isProgrammaticChange) {
            return false;
        }

        this.state.runProgrammaticChange(() => {
            this.model.patchValue({ [controlName]: nextValue }, { emitEvent: false });
            control.markAsUntouched();
            control.markAsPristine();
        });

        return true;
    }

    private shouldKeepValueUntilFilteredOptionsAreReady(
        field: FormField<T>,
        filteredValues: readonly unknown[],
        changedField: string | null,
    ): boolean {
        if (filteredValues.length > 0 || !changedField) {
            return false;
        }

        return resolveReactiveDropdownDependencyBehaviour(field).keepValueUntilOptionsLoaded ||
            (this.isMultiSelect(field) && resolveReactiveDropdownDependencyBehaviour(field).keepValueUntilOptionsLoaded);
    }

    private isFilterDependencyChange(field: FormField<T>, changedField: string | null): boolean {
        if (!changedField) {
            return false;
        }

        return this.dependencyGraph.hasChangedDependency(field.name, [changedField], 'filter');
    }

    private mustClearBecauseDependencyChanged(
        field: FormField<T>,
        changedField: string | null,
        origin: ReactiveChangeOrigin,
    ): boolean {
        if (!this.isFilterDependencyChange(field, changedField)) {
            return false;
        }

        return resolveReactiveDropdownDependencyBehaviour(field).clearOnParentChange &&
            ReactiveChangeOriginPolicy.shouldClearDependentValue(origin);
    }

    private shouldPatchControlValue(
        field: FormField<T>,
        currentValue: unknown,
        nextValue: unknown,
        forceExactClear: boolean,
    ): boolean {
        if (forceExactClear && currentValue !== nextValue) {
            return true;
        }

        return !this.areControlValuesEquivalent(field, currentValue, nextValue);
    }

    private getClearedControlValue(field: FormField<T>): unknown {
        return this.isMultiSelect(field) ? [] : null;
    }


    private mergeSelectedOptionsWhenNeeded(
        field: FormField<T>,
        dropdownData: DropdownData<T>,
        filteredOptions: FilteredValue<T>[],
        currentValue: unknown,
    ): FilteredValue<T>[] {
        if (!this.shouldKeepCurrentOptionsVisible(field) || !ReactiveFormValueUtils.hasNonEmptyValue(currentValue)) {
            return [...filteredOptions];
        }

        const mergedOptions = [...filteredOptions];
        const existingKeys = new Set(
            mergedOptions.flatMap((option) => this.getOptionComparableKeys(field, option.value)),
        );

        for (const option of dropdownData.values ?? []) {
            if (option.value === null || option.value === undefined) {
                continue;
            }

            if (!this.isOptionSelected(field, option as FilteredValue<T>, currentValue)) {
                continue;
            }

            const optionKeys = this.getOptionComparableKeys(field, option.value);
            const alreadyIncluded = optionKeys.some((key) => existingKeys.has(key));

            if (alreadyIncluded) {
                continue;
            }

            mergedOptions.push(option as FilteredValue<T>);
            optionKeys.forEach((key) => existingKeys.add(key));
        }

        return mergedOptions;
    }

    private shouldKeepCurrentOptionsVisible(field: FormField<T>): boolean {
        return resolveReactiveDropdownDependencyBehaviour(field).keepValueUntilOptionsLoaded ||
            resolveReactiveDropdownDependencyBehaviour(field).keepCurrentValueIfStillAvailable ||
            field.dependencyBehaviour?.preserveValueWhenOptionsMissing === true;
    }

    private isOptionSelected(field: FormField<T>, option: FilteredValue<T>, currentValue: unknown): boolean {
        if (Array.isArray(currentValue)) {
            return currentValue.some((value) => this.doesOptionMatchValue(field, option, value));
        }

        return this.doesOptionMatchValue(field, option, currentValue);
    }

    private doesOptionMatchValue(field: FormField<T>, option: FilteredValue<T>, selectedValue: unknown): boolean {
        if (this.areSingleValuesEquivalent(field, selectedValue, option.value)) {
            return true;
        }

        const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(field);
        const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);
        const selectedKeys = this.getSelectedComparableKeys(selectedValue, primaryKey, identityKeys);

        if (selectedKeys.size === 0) {
            return false;
        }

        return ReactiveFormValueUtils.getOptionComparableKeys(
            option as ReactiveDropdownOption,
            primaryKey,
            identityKeys,
        ).some((optionKey) => selectedKeys.has(optionKey));
    }

    private getSelectedComparableKeys(
        selectedValue: unknown,
        primaryKey: string,
        identityKeys: readonly string[],
    ): ReadonlySet<string> {
        const keys = new Set<string>();
        const comparableId = ReactiveFormValueUtils.extractComparableId(selectedValue, primaryKey, identityKeys);

        if (comparableId !== null) {
            this.addComparableKey(keys, comparableId);
        }

        if (selectedValue !== null && selectedValue !== undefined && typeof selectedValue !== 'object') {
            this.addComparableKey(keys, selectedValue);
        }

        return keys;
    }

    private addComparableKey(keys: Set<string>, value: unknown): void {
        const key = String(value ?? '').trim();

        if (key === '' || ReactiveFormValueUtils.isBadDisplayToken(key)) {
            return;
        }

        keys.add(key);
        keys.add(ReactiveFormValueUtils.normalizeComparableKey(key));
    }

    private getOptionComparableKeys(field: FormField<T>, optionValue: unknown): string[] {
        const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(field);
        const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);
        const comparableId = ReactiveFormValueUtils.extractComparableId(optionValue, primaryKey, identityKeys);

        if (comparableId !== null) {
            return [String(comparableId)];
        }

        return [String(optionValue ?? '')].filter((value) => value !== '');
    }

    private getSelectableFilteredOptions(dropdownData: DropdownData<T>): Array<FilteredValue<T>> {
        const filteredValues = dropdownData.filtered ?? [];
        const selectableValues: Array<FilteredValue<T>> = [];

        for (const item of filteredValues) {
            if (item.value === null || item.value === undefined) {
                continue;
            }

            selectableValues.push(item);
        }

        return selectableValues;
    }

    private getRetainedControlValue(field: FormField<T>, currentValue: unknown, filteredOptions: Array<FilteredValue<T>>): unknown {
        if (this.isMultiSelect(field)) {
            return this.getRetainedMultiValue(field, currentValue, filteredOptions);
        }

        return this.getRetainedSingleValue(field, currentValue, filteredOptions);
    }

    private getRetainedSingleValue(field: FormField<T>, currentValue: unknown, filteredOptions: Array<FilteredValue<T>>): unknown {
        if (!ReactiveFormValueUtils.hasNonEmptyValue(currentValue)) {
            return null;
        }

        return this.findMatchingFilteredValue(field, currentValue, filteredOptions);
    }

    private getRetainedMultiValue(field: FormField<T>, currentValue: unknown, filteredOptions: Array<FilteredValue<T>>): unknown[] {
        const currentValues = Array.isArray(currentValue) ? currentValue : [];
        const retainedValues: unknown[] = [];

        for (const value of currentValues) {
            const retainedValue = this.findMatchingFilteredValue(field, value, filteredOptions);

            if (retainedValue !== null) {
                retainedValues.push(retainedValue);
            }
        }

        return retainedValues;
    }

    private findMatchingFilteredValue(
        field: FormField<T>,
        selectedValue: unknown,
        filteredOptions: Array<FilteredValue<T>>,
    ): unknown | null {
        for (const option of filteredOptions) {
            if (this.doesOptionMatchValue(field, option, selectedValue)) {
                return option.value;
            }
        }

        return null;
    }

    private areControlValuesEquivalent(field: FormField<T>, currentValue: unknown, nextValue: unknown): boolean {
        if (this.isMultiSelect(field)) {
            return this.areMultiValuesEquivalent(field, currentValue, nextValue);
        }

        return this.areSingleValuesEquivalent(field, currentValue, nextValue);
    }

    private areMultiValuesEquivalent(field: FormField<T>, currentValue: unknown, nextValue: unknown): boolean {
        const currentValues = Array.isArray(currentValue) ? currentValue : [];
        const nextValues = Array.isArray(nextValue) ? nextValue : [];

        if (currentValues.length !== nextValues.length) {
            return false;
        }

        return currentValues.every((value, index) => this.areSingleValuesEquivalent(field, value, nextValues[index]));
    }

    private areSingleValuesEquivalent(field: FormField<T>, currentValue: unknown, nextValue: unknown): boolean {
        if (currentValue === nextValue) {
            return true;
        }

        const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(field);
        const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);
        const currentComparableValue = this.extractComparableValue(currentValue, primaryKey, identityKeys);
        const nextComparableValue = this.extractComparableValue(nextValue, primaryKey, identityKeys);

        if (currentComparableValue !== null || nextComparableValue !== null) {
            return currentComparableValue !== null && currentComparableValue === nextComparableValue;
        }

        return ReactiveFormValueUtils.areValuesEquivalent(currentValue, nextValue, primaryKey, identityKeys);
    }

    private extractComparableValue(value: unknown, primaryKey: string, identityKeys: readonly string[] = []): string | null {
        const comparableId = ReactiveFormValueUtils.extractComparableId(value, primaryKey, identityKeys);

        if (comparableId !== null) {
            return String(comparableId);
        }

        if (typeof value === 'boolean') {
            return String(value);
        }

        return null;
    }

    private isMultiSelect(field: FormField<T>): boolean {
        return field.input?.type === InputType.MULTISELECT_SEARCH || field.input?.type === InputType.MULTISELECT;
    }
}
